import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, alpha, IconButton, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getVotesProceedings, getVotesProceedingsNavigation } from '../services/api';

const THEME_COLOR = '#3f51b5';

export default function VotesProceedingsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTocItem, setActiveTocItem] = useState(null);
  const [navigation, setNavigation] = useState({ prev: null, next: null });

  // Refs for scroll tracking
  const contentRef = useRef(null);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const [data, nav] = await Promise.all([
          getVotesProceedings(id),
          getVotesProceedingsNavigation(id).catch(() => ({ prev: null, next: null }))
        ]);
        setDocument(data);
        setNavigation(nav);
      } catch (err) {
        console.error('Error fetching votes and proceedings:', err);
        setError('Failed to load votes and proceedings');
      }
      setLoading(false);
    };
    fetchDocument();
  }, [id]);

  const handleNavigate = (docId) => {
    navigate(`/votes-proceedings/${docId}`);
  };

  const formatNavDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Ordinal suffix for parliament number (e.g. 48 -> "48th", 1 -> "1st").
  const ordinalParliament = (n) => {
    const num = parseInt(n, 10);
    if (Number.isNaN(num)) return `${n}`;
    const v = num % 100;
    const suffix = (v >= 11 && v <= 13) ? 'th'
      : (num % 10 === 1) ? 'st'
      : (num % 10 === 2) ? 'nd'
      : (num % 10 === 3) ? 'rd'
      : 'th';
    return `${num}${suffix}`;
  };

  // keywords/themes come back as real JSON arrays of strings, but be tolerant
  // of a Python-repr-style string (e.g. "['a', 'b']") just in case.
  const toStringArray = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => `${v}`.trim()).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim()) {
      const inner = value.trim().replace(/^\[|\]$/g, '');
      return inner
        .split(',')
        .map((v) => v.trim().replace(/^['"]|['"]$/g, '').trim())
        .filter(Boolean);
    }
    return [];
  };

  // A keyword that names a Bill is linkable to the Bills search.
  const isBillKeyword = (kw) => /\bbill\b/i.test(kw);

  // Extract TOC from plain text content - looking for numbered items (e.g., "1 The House met...")
  const tocItems = useMemo(() => {
    if (!document?.content) return [];

    const items = [];
    const lines = document.content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Match numbered sections like "1 The House met" or "12 PETITIONS"
      const numberedMatch = line.match(/^(\d+)\s+(.+)/);
      if (numberedMatch && numberedMatch[2].length > 3 && numberedMatch[2].length < 100) {
        const title = numberedMatch[2];
        // Skip if it looks like a page number or date
        if (!/^\d+$/.test(title) && !/^\d{4}$/.test(title)) {
          items.push({
            id: `toc-section-${i}`,
            number: numberedMatch[1],
            title: title.length > 60 ? title.substring(0, 60) + '...' : title,
            fullTitle: title,
            lineIndex: i
          });
        }
      }
    }

    return items;
  }, [document?.content]);

  // Helper to escape HTML
  const escapeHtml = (text) => {
    const div = window.document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Process content to add section anchors
  const processedContent = useMemo(() => {
    if (!document?.content || tocItems.length === 0) return document?.content || '';

    const lines = document.content.split('\n');
    const processedLines = lines.map((line, index) => {
      const item = tocItems.find(t => t.lineIndex === index);
      if (item) {
        // Wrap the line with a span that has the section ID
        return `<div id="${item.id}" class="toc-section-anchor" style="scroll-margin-top: 100px;">${escapeHtml(line)}</div>`;
      }
      return escapeHtml(line);
    });

    return processedLines.join('\n');
  }, [document?.content, tocItems]);


  // Scroll to a section when clicking TOC item
  const scrollToSection = useCallback((sectionId) => {
    isScrollingRef.current = true;
    setActiveTocItem(sectionId);

    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Reset scrolling flag after animation
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    } else {
      isScrollingRef.current = false;
    }
  }, []);

  // Setup IntersectionObserver to track active section while scrolling
  useEffect(() => {
    if (tocItems.length === 0) return;

    // Set first item as active initially
    if (!activeTocItem && tocItems.length > 0) {
      setActiveTocItem(tocItems[0].id);
    }

    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      // Don't update if user clicked a TOC item (manual scroll in progress)
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTocItem(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all section anchors after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      tocItems.forEach((item) => {
        const element = window.document.getElementById(item.id);
        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [tocItems, activeTocItem]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Document not found'}</Typography>
        <Button component={RouterLink} to="/votes-proceedings" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Votes and Proceedings
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Navigation Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/votes-proceedings"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Votes and Proceedings
        </Button>

        {/* Prev/Next Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={navigation.prev ? `Previous: ${formatNavDate(navigation.prev.date)}` : 'No previous document'}>
            <span>
              <IconButton
                onClick={() => navigation.prev && handleNavigate(navigation.prev.id)}
                disabled={!navigation.prev}
                sx={{
                  bgcolor: navigation.prev ? alpha(THEME_COLOR, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(THEME_COLOR, 0.2) }
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {formatDate(document?.date)}
          </Typography>
          <Tooltip title={navigation.next ? `Next: ${formatNavDate(navigation.next.date)}` : 'No next document'}>
            <span>
              <IconButton
                onClick={() => navigation.next && handleNavigate(navigation.next.id)}
                disabled={!navigation.next}
                sx={{
                  bgcolor: navigation.next ? alpha(THEME_COLOR, 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(THEME_COLOR, 0.2) }
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: THEME_COLOR,
                mb: 0.5
              }}
            >
              {document.title || 'Votes and Proceedings'}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {formatDate(document.date)}
            </Typography>

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label="House of Representatives"
                size="small"
                sx={{ bgcolor: THEME_COLOR, color: 'white' }}
              />
              {document.number && (
                <Chip
                  label={`HVP No. ${document.number}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {document.parliament && (
                <Chip
                  label={`${ordinalParliament(document.parliament)} Parliament`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {document.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
                >
                  View on ParlInfo
                </Button>
              )}
              {document.pdf_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={document.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<PictureAsPdfIcon />}
                >
                  Download PDF
                </Button>
              )}
            </Box>

            {/* Themes */}
            {toStringArray(document.themes).length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Themes
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {toStringArray(document.themes).map((theme) => (
                    <Chip
                      key={`theme-${theme}`}
                      label={theme}
                      size="small"
                      sx={{ bgcolor: alpha(THEME_COLOR, 0.12), color: THEME_COLOR, fontWeight: 500 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Keywords */}
            {toStringArray(document.keywords).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Keywords
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {toStringArray(document.keywords).map((kw) => (
                    isBillKeyword(kw) ? (
                      <Chip
                        key={`kw-${kw}`}
                        label={kw}
                        size="small"
                        variant="outlined"
                        clickable
                        component={RouterLink}
                        to={`/bills?q=${encodeURIComponent(kw)}`}
                        sx={{ borderColor: alpha(THEME_COLOR, 0.4) }}
                      />
                    ) : (
                      <Chip
                        key={`kw-${kw}`}
                        label={kw}
                        size="small"
                        variant="outlined"
                      />
                    )
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 80,
                maxHeight: 'calc(100vh - 120px)',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: THEME_COLOR }}>
                Contents
              </Typography>
              <List dense disablePadding>
                {tocItems.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={activeTocItem === item.id}
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        borderLeft: activeTocItem === item.id
                          ? `3px solid ${THEME_COLOR}`
                          : '3px solid transparent',
                        py: 0.75
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: THEME_COLOR,
                                minWidth: '24px',
                                fontSize: '0.8rem'
                              }}
                            >
                              {item.number}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: activeTocItem === item.id ? 600 : 400
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Main Content */}
        <Grid item xs={12} md={tocItems.length > 0 ? 9 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                '& .toc-section-anchor': {
                  scrollMarginTop: '100px',
                  fontWeight: 600,
                  color: THEME_COLOR,
                  mt: 2,
                  mb: 1
                },
                '& pre': {
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  lineHeight: 1.6
                },
                lineHeight: 1.8,
                fontSize: '0.95rem'
              }}
            >
              {document.content_html ? (
                <div dangerouslySetInnerHTML={{ __html: document.content_html }} />
              ) : tocItems.length > 0 ? (
                <div
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />
              ) : document.content && document.content.trim() ? (
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    m: 0,
                    lineHeight: 1.8
                  }}
                >
                  {document.content}
                </Typography>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 56, color: alpha(THEME_COLOR, 0.4), mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Full text not available for this record
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
                    The full Votes and Proceedings text has not been indexed for this
                    record yet. You can read the complete document on ParlInfo.
                  </Typography>
                  {document.source_url && (
                    <Button
                      variant="contained"
                      href={document.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon />}
                      sx={{ bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#303f9f' } }}
                    >
                      View on ParlInfo
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
